import React, { useState, useContext } from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { Accordion } from 'react-bootstrap';
import AccordionContext from 'react-bootstrap/AccordionContext';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import DocumentHealthTooltip from './DocumentHealthTooltip';
import Styles from './AIDetection.module.scss';

// Custom Accordion Toggle Component
const CategoryToggle = ({ children, eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);
  const isActive = activeEventKey === eventKey;

  return (
    <div
      onClick={decoratedOnClick}
      className={Styles.breakdownItem}
      style={{ cursor: 'pointer' }}
    >
      {children}
    </div>
  );
};

// Component for individual expandable tip - EXACTLY like HealthParameter
const TipItem = ({ tip, index }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(String(index));
  const isActive = activeEventKey === String(index);

  // Extract short title (everything before first colon or period)
  const getTitle = (text) => {
    // Look for first colon
    const colonMatch = text.match(/^([^:]+):/);
    if (colonMatch) {
      return colonMatch[1].trim();
    }

    // Otherwise take first sentence but keep it short
    const firstSentence = text.match(/^[^.!?]+[.!?]/);
    if (firstSentence) {
      const sentence = firstSentence[0].trim();
      return sentence.length > 50 ? sentence.substring(0, 50).trim() + '...' : sentence;
    }

    return text.length > 50 ? text.substring(0, 50).trim() + '...' : text;
  };

  const title = getTitle(tip);
  const details = tip.substring(title.length).trim().replace(/^[:.\s]+/, '');

  return (
    <div className={Styles.tipItem}>
      <div className={Styles.tipHeader} onClick={decoratedOnClick}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <span className={Styles.tipNumber}>{index + 1}</span>
          <span className={Styles.tipSummary}>{title}</span>
        </div>
        <span className={Styles.tipToggle}>
          {isActive ? <IoChevronUp /> : <IoChevronDown />}
        </span>
      </div>
      <Accordion.Collapse eventKey={String(index)}>
        <div className={Styles.tipDetails}>
          {details}
        </div>
      </Accordion.Collapse>
    </div>
  );
};

const AIDetection = ({ aiData, isLoading, highlightingEnabled, onToggleHighlighting }) => {
  if (isLoading) {
    return (
      <div className={Styles.loading}>
        <div className={Styles.spinner} />
        <p>Analyzing content for AI patterns...</p>
      </div>
    );
  }

  if (!aiData) {
    return (
      <div className={Styles.empty}>
        <p>No AI detection data available</p>
      </div>
    );
  }

  const {
    overall_ai_percentage = 0,
    confidence = 'medium',
    breakdown = {},
    tone_profile = {},
    pattern_analysis = {},
    similar_content_detected = [],
    humanization_tips = [],
  } = aiData;

  const {
    ai_generated = 0,
    ai_paraphrased = 0,
    human_paraphrased = 0,
    content_similarity = 0,
    human_written = 0,
  } = breakdown;

  const categories = [
    {
      label: 'AI-generated',
      percentage: ai_generated,
      color: '#F2E8CF',
      icon: '●',
      tooltip: 'Content that appears to be directly generated by AI',
      details: [
        'Generic phrasing and formal tone',
        'Predictable structure',
        'Perfect grammar with no natural errors',
        'Overuse of transition words',
      ],
    },
    {
      label: 'AI-generated & paraphrased',
      percentage: ai_paraphrased,
      color: '#DFD3FC',
      icon: '●',
      tooltip: 'AI content that has been rephrased',
      details: [
        'AI-like structure with varied vocabulary',
        'Formal tone with attempted personality',
        'Restructured common AI phrases',
      ],
    },
    {
      label: 'Human-written & paraphrased',
      percentage: human_paraphrased,
      color: '#D0E0FD',
      icon: '●',
      tooltip: 'Human content that has been edited',
      details: [
        'Personal perspective with edited clarity',
        'Natural voice with polish',
        'Authentic insights with refined wording',
      ],
    },
    {
      label: 'Content-Similarity',
      percentage: content_similarity,
      color: '#FDD0D0',
      icon: '●',
      tooltip: 'Text matching existing online content',
      details: [
        'Phrases that match common online content',
        'Overused industry jargon',
        'Clichéd expressions',
      ],
    },
    {
      label: 'Human-written',
      percentage: human_written,
      color: '#F5F6F6',
      icon: '●',
      tooltip: 'Original content with unique voice',
      details: [
        'Natural speech patterns and rhythm',
        'Personal anecdotes and specific examples',
        'Authentic personality and humor',
      ],
    },
  ];

  return (
    <div className={Styles.aiDetection}>
      {/* Overall Score Section */}
      <div className={Styles.overallSection}>
        <div className={Styles.percentageDisplay}>
          <span className={Styles.percentage}>{overall_ai_percentage}</span>
          <span className={Styles.percentSign}>%</span>
        </div>
        <p className={Styles.subtext}>of text is likely AI-generated</p>
      </div>

      {/* Highlighting Toggle */}
      <div className={Styles.highlightToggle}>
        <label className={Styles.toggleLabel}>
          <input
            type="checkbox"
            checked={highlightingEnabled}
            onChange={onToggleHighlighting}
            className={Styles.toggleCheckbox}
          />
          <span className={Styles.toggleSlider}></span>
          <span className={Styles.toggleText}>Highlight AI sections in editor</span>
        </label>
      </div>

      {/* Progress Bar */}
      <div className={Styles.progressBar}>
        {categories.map((cat, index) => (
          cat.percentage > 0 && (
            <div
              key={index}
              className={Styles.progressSegment}
              style={{
                width: `${cat.percentage}%`,
                backgroundColor: cat.color,
              }}
            />
          )
        ))}
      </div>

      {/* Breakdown List */}
      <Accordion className={Styles.breakdown}>
        {categories.map((cat, index) => (
          <div key={index}>
            <CategoryToggle eventKey={String(index)}>
              <div className={Styles.breakdownLeft}>
                <span
                  className={Styles.indicator}
                  style={{ color: cat.color }}
                >
                  {cat.icon}
                </span>
                <span className={Styles.label}>{cat.label}</span>
                <DocumentHealthTooltip content={cat.tooltip} placement="top">
                  <span onClick={(e) => e.stopPropagation()}>
                    <AiOutlineQuestionCircle className={Styles.infoIcon} />
                  </span>
                </DocumentHealthTooltip>
              </div>
              <span className={Styles.percentage}>{cat.percentage}%</span>
            </CategoryToggle>
            <Accordion.Collapse eventKey={String(index)}>
              <div className={Styles.categoryDetails}>
                <ul>
                  {cat.details.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              </div>
            </Accordion.Collapse>
          </div>
        ))}
      </Accordion>

      {/* Tone Profile */}
      {tone_profile && Object.keys(tone_profile).length > 0 && (
        <div className={Styles.toneProfile}>
          <div className={Styles.toneHeader}>
            <span className={Styles.toneTitle}>Content Tone Profile</span>
            <DocumentHealthTooltip
              content="Scores from 1-10 analyzing your content's style and authenticity"
              placement="top"
            >
              <span>
                <AiOutlineQuestionCircle className={Styles.infoIcon} />
              </span>
            </DocumentHealthTooltip>
          </div>
          <div className={Styles.toneMetrics}>
            {tone_profile.formality_score !== undefined && (
              <div className={Styles.toneMetric}>
                <span className={Styles.metricLabel}>Formality</span>
                <div className={Styles.metricBar}>
                  <div
                    className={Styles.metricFill}
                    style={{ width: `${(tone_profile.formality_score / 10) * 100}%` }}
                  />
                </div>
                <span className={Styles.metricValue}>{tone_profile.formality_score}/10</span>
              </div>
            )}
            {tone_profile.personality_score !== undefined && (
              <div className={Styles.toneMetric}>
                <span className={Styles.metricLabel}>Personality</span>
                <div className={Styles.metricBar}>
                  <div
                    className={Styles.metricFill}
                    style={{ width: `${(tone_profile.personality_score / 10) * 100}%` }}
                  />
                </div>
                <span className={Styles.metricValue}>{tone_profile.personality_score}/10</span>
              </div>
            )}
            {tone_profile.emotional_range !== undefined && (
              <div className={Styles.toneMetric}>
                <span className={Styles.metricLabel}>Emotional Range</span>
                <div className={Styles.metricBar}>
                  <div
                    className={Styles.metricFill}
                    style={{ width: `${(tone_profile.emotional_range / 10) * 100}%` }}
                  />
                </div>
                <span className={Styles.metricValue}>{tone_profile.emotional_range}/10</span>
              </div>
            )}
            {tone_profile.authenticity_score !== undefined && (
              <div className={Styles.toneMetric}>
                <span className={Styles.metricLabel}>Authenticity</span>
                <div className={Styles.metricBar}>
                  <div
                    className={Styles.metricFill}
                    style={{ width: `${(tone_profile.authenticity_score / 10) * 100}%` }}
                  />
                </div>
                <span className={Styles.metricValue}>{tone_profile.authenticity_score}/10</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pattern Analysis */}
      {pattern_analysis && Object.keys(pattern_analysis).length > 0 && (
        <div className={Styles.patternAnalysis}>
          <div className={Styles.patternHeader}>
            <span className={Styles.patternTitle}>Content Patterns Detected</span>
            <DocumentHealthTooltip
              content="Specific linguistic patterns found in your content"
              placement="top"
            >
              <span>
                <AiOutlineQuestionCircle className={Styles.infoIcon} />
              </span>
            </DocumentHealthTooltip>
          </div>
          <div className={Styles.patternGrid}>
            {pattern_analysis.transition_words_detected && pattern_analysis.transition_words_detected.length > 0 && (
              <div className={Styles.patternItem}>
                <span className={Styles.patternLabel}>Transition Words</span>
                <span className={Styles.patternValue}>{pattern_analysis.transition_words_detected.slice(0, 5).join(', ')}</span>
              </div>
            )}
            {pattern_analysis.hedge_phrases_count !== undefined && (
              <div className={Styles.patternItem}>
                <span className={Styles.patternLabel}>Hedge Phrases</span>
                <span className={Styles.patternValue}>{pattern_analysis.hedge_phrases_count} found</span>
              </div>
            )}
            {pattern_analysis.generic_statements_count !== undefined && (
              <div className={Styles.patternItem}>
                <span className={Styles.patternLabel}>Generic Statements</span>
                <span className={Styles.patternValue}>{pattern_analysis.generic_statements_count} found</span>
              </div>
            )}
            {pattern_analysis.specific_examples_count !== undefined && (
              <div className={Styles.patternItem}>
                <span className={Styles.patternLabel}>Specific Examples</span>
                <span className={Styles.patternValue}>{pattern_analysis.specific_examples_count} found</span>
              </div>
            )}
            {pattern_analysis.sentence_length_variance && (
              <div className={Styles.patternItem}>
                <span className={Styles.patternLabel}>Sentence Variance</span>
                <span className={Styles.patternValue}>{pattern_analysis.sentence_length_variance}</span>
              </div>
            )}
            {pattern_analysis.vocabulary_diversity && (
              <div className={Styles.patternItem}>
                <span className={Styles.patternLabel}>Vocabulary Diversity</span>
                <span className={Styles.patternValue}>{pattern_analysis.vocabulary_diversity}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Humanization Tips */}
      {humanization_tips.length > 0 && (
        <div className={Styles.tipsSection}>
          <div className={Styles.tipsHeader}>
            <span className={Styles.tipsTitle}>Humanization Tips</span>
            <DocumentHealthTooltip
              content="Actionable suggestions to make your content sound more natural and human"
              placement="top"
            >
              <span>
                <AiOutlineQuestionCircle className={Styles.infoIcon} />
              </span>
            </DocumentHealthTooltip>
          </div>
          <Accordion className={Styles.tipsAccordion}>
            {humanization_tips.map((tip, index) => (
              <TipItem key={index} tip={tip} index={index} />
            ))}
          </Accordion>
        </div>
      )}

      {/* Similar Content */}
      {similar_content_detected.length > 0 && (
        <div className={Styles.similarContent}>
          <div className={Styles.similarHeader}>
            <span className={Styles.similarTitle}>Similar Content</span>
            <DocumentHealthTooltip
              content="Sections of your content that match existing online sources"
              placement="top"
            >
              <span>
                <AiOutlineQuestionCircle className={Styles.infoIcon} />
              </span>
            </DocumentHealthTooltip>
          </div>
          <div className={Styles.similarLinks}>
            {similar_content_detected.map((content, index) => {
              // Validate URL before rendering
              if (!content.url || typeof content.url !== 'string') {
                return null;
              }

              // Try to extract hostname safely
              let hostname = '';
              try {
                hostname = new URL(content.url).hostname;
              } catch (e) {
                // If URL parsing fails, extract domain manually or skip
                const match = content.url.match(/^(?:https?:\/\/)?(?:www\.)?([^\/]+)/i);
                hostname = match ? match[1] : 'Unknown source';
              }

              return (
                <div key={index} className={Styles.similarItem}>
                  <a
                    href={content.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.link}
                  >
                    {content.url}
                  </a>
                  <span className={Styles.domain}>
                    {hostname}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIDetection;
